import React, { useEffect, useMemo, useState } from "react";
import { Tab, Tabs } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import styles from "./KeywordTopFacultySection.module.scss";

const getInitials = (name) => {
  if (!name || typeof name !== "string") return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

/** Shouting API labels (e.g. PUBLIC HEALTH) → Title Case for tab display. */
const formatTopicTabLabel = (keyword) => {
  if (!keyword || typeof keyword !== "string") return keyword;
  const s = keyword.trim();
  if (!s) return s;
  const isAllCaps = s === s.toUpperCase() && /[A-Z]/.test(s);
  if (!isAllCaps) return s;
  return s
    .toLowerCase()
    .split(/\s+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
};

/**
 * Merge faculty_publication_counts across institutions for one keyword bucket.
 * Sums publication_count when the same faculty id appears more than once.
 */
const aggregateTopFacultyForKeywordEntry = (keywordEntry) => {
  const keyword = Object.keys(keywordEntry)[0];
  const institutions = keywordEntry[keyword] || [];
  const map = new Map();

  institutions.forEach((inst) => {
    const list = inst.faculty_publication_counts;
    if (!Array.isArray(list)) return;
    list.forEach((f) => {
      if (!f?.id) return;
      const n = f.publication_count || 0;
      const existing = map.get(f.id);
      if (existing) {
        existing.publication_count += n;
        if (f.photo_url && !existing.photo_url) existing.photo_url = f.photo_url;
        if (f.name && !existing.name) existing.name = f.name;
      } else {
        map.set(f.id, {
          id: f.id,
          name: f.name || "—",
          photo_url: f.photo_url || null,
          publication_count: n,
        });
      }
    });
  });

  return Array.from(map.values())
    .sort((a, b) => b.publication_count - a.publication_count)
    .slice(0, 20);
};

const FacultyRow = ({ faculty, maxCount, barColor }) => {
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => {
    setImgFailed(false);
  }, [faculty.photo_url, faculty.id]);

  const pct =
    maxCount > 0
      ? Math.max((faculty.publication_count / maxCount) * 100, 2.5)
      : 0;

  return (
    <div className={styles.row}>
      {faculty.photo_url && !imgFailed ? (
        <img
          className={styles.avatar}
          src={faculty.photo_url}
          alt=""
          loading="lazy"
          onError={() => setImgFailed(true)}
        />
      ) : (
        <div className={styles["avatar-fallback"]} aria-hidden>
          {getInitials(faculty.name)}
        </div>
      )}
      <div className={styles.name} title={faculty.name}>
        {faculty.name}
      </div>
      <div className={styles["bar-wrap"]}>
        <div
          className={styles["bar-fill"]}
          style={{ width: `${pct}%`, backgroundColor: barColor || undefined }}
        />
      </div>
      <div className={styles.count}>
        {faculty.publication_count.toLocaleString()}
      </div>
    </div>
  );
};

const KeywordTopFacultySection = ({
  rawData,
  entityLabel = "institutions",
  barColor,
}) => {
  const isDesktop = useMediaQuery("(min-width: 900px)");

  const keywords = useMemo(
    () => (rawData || []).map((entry) => Object.keys(entry)[0]),
    [rawData]
  );

  const rowsByKeyword = useMemo(
    () => (rawData || []).map((entry) => aggregateTopFacultyForKeywordEntry(entry)),
    [rawData]
  );

  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => {
    setTabIndex(0);
  }, [rawData]);

  const hasAnyFaculty = useMemo(
    () => rowsByKeyword.some((rows) => rows.length > 0),
    [rowsByKeyword]
  );

  const displayIndex = useMemo(() => {
    if (!keywords.length) return 0;
    return Math.min(Math.max(0, tabIndex), keywords.length - 1);
  }, [keywords.length, tabIndex]);

  const currentRows = rowsByKeyword[displayIndex] || [];
  const maxCount = currentRows[0]?.publication_count || 1;

  const handleTabChange = (_, newValue) => {
    setTabIndex(newValue);
  };

  if (!rawData?.length || !hasAnyFaculty) return null;

  return (
    <section className={styles.section} aria-labelledby="keyword-top-faculty-heading">
      <h3 id="keyword-top-faculty-heading" className={styles.title}>
        Top faculty by research topic in those {entityLabel}
      </h3>

      <div
        className={`${styles.layout} ${isDesktop ? styles["layout--desktop"] : styles["layout--mobile"]}`}
      >
        <Tabs
          orientation={isDesktop ? "vertical" : "horizontal"}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          value={displayIndex}
          onChange={handleTabChange}
          aria-label="Research topics"
          classes={{
            root: `${styles.vtabs} ${isDesktop ? "" : styles["vtabs--horizontal"]}`,
            indicator: styles["vtabs-indicator"],
          }}
        >
          {keywords.map((kw, i) => (
            <Tab
              key={kw}
              label={formatTopicTabLabel(kw)}
              value={i}
              sx={{ textTransform: "none" }}
              classes={{
                root: styles["vtab-item"],
                selected: styles["vtab-item-selected"],
              }}
            />
          ))}
        </Tabs>

        <div className={styles["chart-panel"]}>
          {currentRows.length === 0 ? (
            <div className={styles.empty}>
              No faculty publication breakdown for this topic.
            </div>
          ) : (
            <div className={styles.rows}>
              {currentRows.map((faculty) => (
                <FacultyRow
                  key={faculty.id}
                  faculty={faculty}
                  maxCount={maxCount}
                  barColor={barColor}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default KeywordTopFacultySection;
